import React, { useState, useEffect } from 'react';
import { stockMovementAPI } from '../services/stockMovementApi';
import { productAPI } from '../services/productApi';
import { StockMovement, Product } from '../types';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import Layout from '../components/Layout';
import { History, Search, AlertCircle, X, ArrowUpRight, ArrowDownLeft, RefreshCcw, Lock, Unlock } from 'lucide-react';

const StockMovementsPage: React.FC = () => {
    const [movements, setMovements] = useState<StockMovement[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const { selectedCompany } = useCompany();
    const { t, isRTL } = useI18n();

    useEffect(() => {
        if (selectedCompany.selectedCompanyId) {
            loadData();
        } else {
            setMovements([]);
            setProducts([]);
            setLoading(false);
        }
    }, [selectedCompany.selectedCompanyId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [movementsData, productsData] = await Promise.all([
                stockMovementAPI.getMovementsByCompany(selectedCompany.selectedCompanyId!),
                productAPI.getProductsByCompany(selectedCompany.selectedCompanyId!)
            ]);
            setMovements(movementsData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
            setProducts(productsData);
            setError(null);
        } catch (err) {
            setError(t('stock.movements.failedToLoad'));
            console.error('Error loading movements:', err);
        } finally {
            setLoading(false);
        }
    };

    const getProductName = (productId: string) => {
        return products.find(p => p.id === productId)?.name || productId;
    };

    const getMovementIcon = (type: string) => {
        switch (type) {
            case 'IN': return <ArrowDownLeft className="text-green-500" />;
            case 'OUT': return <ArrowUpRight className="text-red-500" />;
            case 'RESERVED': return <Lock className="text-yellow-500" />;
            case 'RELEASED': return <Unlock className="text-blue-500" />;
            case 'ADJUSTMENT': return <RefreshCcw className="text-gray-500" />;
            default: return null;
        }
    };

    const filteredMovements = movements.filter(m =>
        getProductName(m.productId)?.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
        (m?.orderId && m.orderId?.toLowerCase().includes(searchTerm?.toLowerCase() || ''))
    );

    if (!selectedCompany.selectedCompanyId) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-64">
                    <div className="text-center">
                        <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('clients.noCompanySelected')}</h2>
                        <p className="text-gray-600 mb-4">{t('clients.noCompanyMessage')}</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={`flex justify-between items-center mb-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">{t('stock.movements.title')}</h1>
                    <p className="text-gray-600 mt-1">
                        {t('stock.movements.subtitle', { companyName: selectedCompany.selectedCompany?.name })}
                    </p>
                </div>
                <button
                    onClick={loadData}
                    className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                    title={t('common.refresh')}
                >
                    <RefreshCcw size={24} />
                </button>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}><X size={18} /></button>
                </div>
            )}

            <div className="mb-6">
                <div className="relative">
                    <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 z-10`} size={20} />
                    <input
                        type="text"
                        placeholder={t('common.search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                </div>
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.movements.date')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.movements.type')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.movements.product')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.movements.quantity')}
                                </th>
                                <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                    {t('stock.movements.order')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredMovements.map((m) => (
                                <tr key={m.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(m.createdAt).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            {getMovementIcon(m.movementType)}
                                            <span className="text-sm font-medium text-gray-900">
                                                {t(`stock.movements.${m?.movementType?.toLowerCase()}`)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {getProductName(m.productId)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold">
                                        <span className={
                                            m.movementType === 'IN' ||
                                                m.movementType === 'RELEASED' ||
                                                (m.movementType === 'ADJUSTMENT' && m.quantity > 0)
                                                ? 'text-green-600' : 'text-red-600'
                                        }>
                                            {m.movementType === 'ADJUSTMENT'
                                                ? (m.quantity > 0 ? '+' : '')
                                                : (m.movementType === 'OUT' || m.movementType === 'RESERVED' ? '-' : '+')}
                                            {m.movementType === 'ADJUSTMENT' ? m.quantity : m.quantity}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {m.orderId || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filteredMovements.length === 0 && !loading && (
                    <div className="text-center py-8 text-gray-500">
                        {t('stock.movements.noMovementsFound')}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default StockMovementsPage;
